/**
 * FeesScreen — lista de expensas + ver detalle / marcar pagada.
 * A = abrir detalle, B = back.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useApp } from '@/state/store';
import { sfx } from '@/lib/audio';
import { feesApi, type Fee, ApiError } from '@/lib/api';
import { CoinSprite, LoadingSprite } from '@/components/Sprites';

function statusVariant(status: Fee['status']): 'success' | 'danger' | 'pending' {
  switch (status) {
    case 'paid':     return 'success';
    case 'overdue':  return 'danger';
    default:         return 'pending';
  }
}

function statusLabel(status: Fee['status']): string {
  return status === 'paid' ? 'OK'
       : status === 'overdue' ? '!'
       : 'PDTE';
}

export function FeesScreen() {
  const { goto, openDetail } = useApp();
  const [items, setItems] = useState<Fee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(0);
  const handlersRef = useRef({ items, selected, openDetail });
  handlersRef.current = { items, selected, openDetail };

  const load = useCallback(async () => {
    try {
      const res = await feesApi.list();
      setItems(res.fees || []);
    } catch (err) {
      if (err instanceof ApiError) console.error('Fees load:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = (delta: number) => {
    if (items.length === 0) return;
    const next = Math.max(0, Math.min(items.length - 1, selected + delta));
    if (next !== selected) {
      setSelected(next);
      sfx.tick();
    }
  };

  const total = items.reduce((s, f) => (f.status === 'paid' ? s : s + f.amount), 0);
  const pending = items.filter(f => f.status !== 'paid').length;

  useEffect(() => {
    const onUp = () => move(-1);
    const onDown = () => move(1);
    const onA = () => {
      const h = handlersRef.current;
      const item = h.items[h.selected];
      if (item) { sfx.confirm(); h.openDetail({ id: item.id, type: 'fee' }); }
    };
    const onB = () => { sfx.cancel(); goto('home'); };
    window.addEventListener('vicin-gb:up', onUp);
    window.addEventListener('vicin-gb:down', onDown);
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:up', onUp);
      window.removeEventListener('vicin-gb:down', onDown);
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, [goto]);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <CoinSprite size={11} />
          <span className="gb-pix gb-pix-xs">
            GASTOS · {pending > 0 ? `${pending} PEND` : `${items.length} TOTAL`}
          </span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5 }}>
            [A PAGAR]
          </span>
        </div>
      </div>

      <div className="gb-body" style={{ padding: '0', gap: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <LoadingSprite size={32} />
            <div className="gb-pix gb-pix-xs gb-mid-text">CARGANDO...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <CoinSprite size={48} />
            <div className="gb-pix gb-pix-xs gb-mid-text">SIN GASTOS</div>
            <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, maxWidth: 200, textAlign: 'center' }}>
              No tenés expensas pendientes.
            </div>
          </div>
        ) : (
          <>
            {pending > 0 && (
              <div
                style={{
                  padding: '3px 6px',
                  background: 'var(--gb-dark)',
                  color: 'var(--gb-bg)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontFamily: 'var(--pixel)',
                  fontSize: 5,
                  flexShrink: 0,
                }}
              >
                <span>{pending} PENDIENTE{pending > 1 ? 'S' : ''}</span>
                <span style={{ fontSize: 7 }}>${total}</span>
              </div>
            )}
            <div className="gb-list" style={{ overflowY: 'auto', flex: 1 }}>
              {items.map((f, i) => {
                const sel = i === selected;
                return (
                  <button
                    key={f.id}
                    className="gb-list-item"
                    data-selected={sel}
                    onClick={() => { setSelected(i); sfx.confirm(); openDetail({ id: f.id, type: 'fee' }); }}
                    onMouseEnter={() => setSelected(i)}
                    data-testid={`fee-${f.id}`}
                  >
                  <div className="gb-list-row">
                    <span className={`gb-list-badge gb-list-badge--${statusVariant(f.status)}`} style={{ fontSize: 5 }}>
                      {statusLabel(f.status)}
                    </span>
                    <span className="gb-list-title">{f.description || `GASTO ${f.period}`}</span>
                    <span className="gb-pix" style={{ fontSize: 7, color: 'var(--gb-dark)' }}>
                      ${f.amount}
                    </span>
                  </div>
                  <div className="gb-list-row gb-list-meta">
                    <span>VTO {f.due_date}</span>
                  </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}